import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import apiService from '../services/api';
import VersionInfo from './VersionInfo';

const UserSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [settingData, setSettingData] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        theme: 'light',
        timezone: 'EAT',
        language: 'en',
        default_sales_rep_screen: 'dashboard',
        allow_theme_change: false,
        allow_language_change: false
    });

    useEffect(() => {
        loadUserSettings();
    }, []);

    const loadUserSettings = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiService.getOrganizationDefaultSettingByCode('user_settings');
            if (response.success && response.data) {
                setSettingData(response.data);
                const settings = response.data.value || {};
                setFormData({
                    theme: settings.theme || 'light',
                    timezone: settings.timezone || 'EAT',
                    language: settings.language || 'en',
                    default_sales_rep_screen: settings.default_sales_rep_screen || 'dashboard',
                    allow_theme_change: settings.allow_theme_change || false,
                    allow_language_change: settings.allow_language_change || false
                });
            } else {
                setError(response.error || 'Failed to load user settings');
            }
        } catch (error) {
            console.error('Error loading user settings:', error);
            setError('Failed to load user settings');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const updateData = {
                name: 'User Settings',
                code: 'user_settings',
                value: formData
            };

            let response;
            if (settingData && settingData.id) {
                // Update existing setting
                response = await apiService.updateOrganizationDefaultSetting(settingData.id, updateData);
            } else {
                // Create new setting
                response = await apiService.createOrganizationDefaultSetting(updateData);
            }

            if (response.success) {
                setSuccess('User settings saved successfully!');
                setSettingData(response.data);
                setTimeout(() => setSuccess(''), 3000);
            } else {
                setError(response.error || 'Failed to save user settings');
            }
        } catch (error) {
            console.error('Error saving user settings:', error);
            setError('Failed to save user settings');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <div className="mt-2">Loading user settings...</div>
            </div>
        );
    }

    return (
        <div>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>
                            <i className="bi bi-person-gear me-2"></i>
                            User Settings
                        </h2>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {success && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                            <i className="bi bi-check-circle me-2"></i>
                            {success}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Row>
                <Col lg={8}>
                    <Card>
                        <Card.Header>
                            <h5 className="mb-0">
                                <i className="bi bi-sliders me-2"></i>
                                Default User Interface Settings
                            </h5>
                        </Card.Header>
                        <Form onSubmit={handleSave}>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Theme</Form.Label>
                                            <Form.Select
                                                name="theme"
                                                value={formData.theme}
                                                onChange={handleInputChange}
                                            >
                                                <option value="light">Light</option>
                                                <option value="dark">Dark</option>
                                                <option value="auto">Auto (System Preference)</option>
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Default theme for new users
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Timezone</Form.Label>
                                            <Form.Select
                                                name="timezone"
                                                value={formData.timezone}
                                                onChange={handleInputChange}
                                            >
                                                <option value="EAT">East Africa Time (EAT)</option>
                                                <option value="UTC">Coordinated Universal Time (UTC)</option>
                                                <option value="EST">Eastern Standard Time (EST)</option>
                                                <option value="PST">Pacific Standard Time (PST)</option>
                                                <option value="GMT">Greenwich Mean Time (GMT)</option>
                                                <option value="CET">Central European Time (CET)</option>
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Default timezone for new users
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Language</Form.Label>
                                            <Form.Select
                                                name="language"
                                                value={formData.language}
                                                onChange={handleInputChange}
                                            >
                                                <option value="en">English</option>
                                                <option value="es">Spanish</option>
                                                <option value="fr">French</option>
                                                <option value="de">German</option>
                                                <option value="sw">Swahili</option>
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Default language for new users
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Default Sales Rep Screen</Form.Label>
                                            <Form.Select
                                                name="default_sales_rep_screen"
                                                value={formData.default_sales_rep_screen}
                                                onChange={handleInputChange}
                                            >
                                                <option value="dashboard">Dashboard</option>
                                                <option value="customers">Customers</option>
                                                <option value="products">Products</option>
                                                <option value="orders">Orders</option>
                                                <option value="reports">Reports</option>
                                            </Form.Select>
                                            <Form.Text className="text-muted">
                                                Initial screen shown to sales representatives
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <hr />

                                <h6 className="mb-3">
                                    <i className="bi bi-toggles me-2"></i>
                                    User Permissions
                                </h6>

                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="allow_theme_change"
                                                name="allow_theme_change"
                                                checked={formData.allow_theme_change}
                                                onChange={handleInputChange}
                                                label="Allow users to change theme"
                                            />
                                            <Form.Text className="text-muted">
                                                If enabled, users can switch between light/dark themes
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                id="allow_language_change"
                                                name="allow_language_change"
                                                checked={formData.allow_language_change}
                                                onChange={handleInputChange}
                                                label="Allow users to change language"
                                            />
                                            <Form.Text className="text-muted">
                                                If enabled, users can select their preferred language
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>

                            <Card.Footer>
                                <div className="d-flex justify-content-end">
                                    <Button variant="primary" type="submit" disabled={saving}>
                                        {saving ? (
                                            <>
                                                <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-lg me-2"></i>
                                                Save Settings
                                            </>
                                        )}
                                    </Button>
                                </div>
                            </Card.Footer>
                        </Form>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="bg-light mb-3">
                        <Card.Header>
                            <h6 className="mb-0">
                                <i className="bi bi-info-circle me-2"></i>
                                About User Settings
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <p className="mb-2">
                                <strong>Theme:</strong> Controls the default appearance of the application interface.
                            </p>
                            <p className="mb-2">
                                <strong>Timezone:</strong> Sets the default timezone for displaying dates and times.
                            </p>
                            <p className="mb-2">
                                <strong>Language:</strong> Determines the default language for the user interface.
                            </p>
                            <p className="mb-2">
                                <strong>Default Screen:</strong> The first screen sales representatives see when logging in.
                            </p>
                            <p className="mb-0">
                                <strong>Permissions:</strong> Control what users can customize in their personal settings.
                            </p>
                        </Card.Body>
                    </Card>

                    {/* Version Information */}
                    <VersionInfo />
                </Col>
            </Row>
        </div>
    );
};

export default UserSettings;